/* ================================================
   ANTOINE GERBER — curseur « / »
   Un slash en gras suit la souris. Il fait un tour
   complet à l'entrée sur un élément cliquable.

   La couleur s'inverse par DÉTECTION du fond sous le
   curseur (classe .is-inverted), et non par
   mix-blend-mode : ce dernier force la page dans un
   mode de composition qui fait disparaître du contenu
   sur les pages à galerie collante (pages produit),
   sur Chrome comme sur Safari.
   ================================================ */
(() => {
  const fine = window.matchMedia('(hover: hover) and (pointer: fine)');
  const calme = window.matchMedia('(prefers-reduced-motion: reduce)');

  // Pas de curseur personnalisé au tactile ni en mode animations réduites :
  // on laisse le curseur natif du système.
  if (!fine.matches || calme.matches) return;

  const CIBLES = [
    'a[href]',
    'button',
    'summary',
    'label[for]',
    'select',
    '[role="button"]',
    '[role="link"]',
    'input[type="submit"]',
    'input[type="button"]',
    'input[type="checkbox"]',
    'input[type="radio"]',
    '.load-all__button',
    '.product-grid__item',
    '.collection-subnav__item',
    '[onclick]',
  ].join(',');

  const racine = document.documentElement;
  const curseur = document.createElement('div');
  curseur.className = 'ag-cursor';
  curseur.setAttribute('aria-hidden', 'true');

  const glyphe = document.createElement('span');
  glyphe.className = 'ag-cursor__glyph';
  glyphe.textContent = '/';
  curseur.appendChild(glyphe);
  document.body.appendChild(curseur);

  // Signale au CSS de masquer le curseur natif. Posé par JS uniquement :
  // sans JS, l'utilisateur garde son curseur système.
  racine.classList.add('ag-cursor-on');

  /* --- Passage dans le « top layer » ------------------------------------

     Les tiroirs panier et favoris, la recherche et l'ajout rapide sont des
     <dialog> ouverts par showModal(). Le navigateur les peint dans le top
     layer, une couche qui passe AU-DESSUS de n'importe quel z-index, même
     2 147 483 000. Le glyphe se retrouvait donc caché derrière le tiroir,
     alors que `cursor: none` masquait toujours le curseur système : plus
     aucun curseur visible.

     Un élément `popover` est peint dans ce même top layer. On y promeut le
     curseur, et on l'y remet à chaque ouverture de dialogue : à l'intérieur
     du top layer, le dernier entré est peint au-dessus.

     Si le navigateur ne connaît pas l'API popover, on rend simplement le
     curseur système dans les dialogues (voir custom-cursor.css) : mieux
     vaut une flèche que rien. */

  const supportePopover =
    typeof curseur.showPopover === 'function' &&
    Object.prototype.hasOwnProperty.call(HTMLElement.prototype, 'popover');

  if (supportePopover) {
    curseur.setAttribute('popover', 'manual');
    const montrer = () => {
      try {
        curseur.hidePopover();
      } catch (e) {
        /* pas encore affiché */
      }
      try {
        curseur.showPopover();
      } catch (e) {
        /* le navigateur refuse : on garde le rendu normal */
      }
    };
    montrer();

    let planifie = false;
    const remonter = () => {
      if (planifie) return;
      planifie = true;
      requestAnimationFrame(() => {
        planifie = false;
        if (document.querySelector('dialog[open]')) montrer();
      });
    };

    new MutationObserver((mutations) => {
      for (const m of mutations) {
        if (m.type === 'attributes' && m.attributeName === 'open') return remonter();
        if (m.type === 'childList' && m.addedNodes.length) return remonter();
      }
    }).observe(document.documentElement, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['open'],
    });
  } else {
    racine.classList.add('ag-cursor-sans-top-layer');
  }

  let cibleX = window.innerWidth / 2;
  let cibleY = window.innerHeight / 2;
  let x = cibleX;
  let y = cibleY;
  let visible = false;
  let survole = null;

  const afficher = () => {
    if (visible) return;
    visible = true;
    // On recale la position avant d'afficher, sinon le glyphe traverse
    // l'écran depuis son ancienne position.
    x = cibleX;
    y = cibleY;
    curseur.classList.add('is-visible');
  };

  window.addEventListener(
    'pointermove',
    (e) => {
      if (e.pointerType !== 'mouse') return;
      cibleX = e.clientX;
      cibleY = e.clientY;
      afficher();
    },
    { passive: true }
  );

  // Sortie de la fenêtre : on efface plutôt que de laisser le glyphe collé au bord.
  document.addEventListener('mouseleave', () => {
    visible = false;
    curseur.classList.remove('is-visible');
  });

  window.addEventListener(
    'pointerover',
    (e) => {
      if (e.pointerType && e.pointerType !== 'mouse') return;
      const cible = e.target instanceof Element ? e.target.closest(CIBLES) : null;
      if (cible === survole) return;
      survole = cible;

      if (!cible) {
        curseur.classList.remove('is-link');
        return;
      }

      curseur.classList.add('is-link');
      // Retirer puis reforcer un reflow relance l'animation même si on passe
      // directement d'un lien à un autre.
      curseur.classList.remove('is-spinning');
      void curseur.offsetWidth;
      curseur.classList.add('is-spinning');
    },
    { passive: true }
  );

  glyphe.addEventListener('animationend', () => {
    curseur.classList.remove('is-spinning');
  });

  window.addEventListener('pointerdown', () => curseur.classList.add('is-pressed'), { passive: true });
  window.addEventListener('pointerup', () => curseur.classList.remove('is-pressed'), { passive: true });

  /* --- Détection du fond ------------------------------------------------ */

  // Luminance perçue (Rec. 709). En dessous de 0.5 le fond est considéré sombre.
  const luminance = (r, v, b) => (0.2126 * r + 0.7152 * v + 0.0722 * b) / 255;

  // Le footer bascule en sombre via `filter: invert(1)` : les couleurs
  // calculées restent celles du mode clair. On compte les inversions
  // empilées au-dessus du point survolé ; un nombre impair signifie que
  // ce qu'on voit est l'inverse de ce que le style déclare.
  const inversionsAuDessus = (depart) => {
    let n = 0;
    let e = depart;
    let garde = 0;

    while (e && garde++ < 30) {
      const f = getComputedStyle(e).filter;
      if (f && f !== 'none' && f.indexOf('invert(') !== -1) {
        const trouves = f.match(/invert\([^)]+\)/g) || [];
        for (const inv of trouves) {
          const valeur = parseFloat(inv.replace(/invert\(|\)|%/g, ''));
          if (valeur >= 0.5) n++;
        }
      }
      e = e.parentElement;
    }
    return n;
  };

  const fondSombre = (px, py) => {
    const depart = document.elementFromPoint(px, py);

    let inverse = inversionsAuDessus(depart) % 2 === 1;

    // Le footer en mode sombre porte la classe `footer-mode--inverted`.
    // On se fie à la classe plutôt qu'au mécanisme : selon les versions, le
    // basculement passe par un filtre ou par de vraies couleurs, et les
    // couleurs calculées ne reflètent alors pas ce qu'on voit.
    // Le bouton MODE lui-même est ré-inversé par le thème pour garder ses
    // couleurs propres : il ne doit donc pas être compté.
    if (depart && depart.closest) {
      if (depart.closest('.footer-mode--inverted') && !depart.closest('.footer-mode-toggle')) {
        inverse = !inverse;
      }
    }

    let el = depart;
    let garde = 0;

    while (el && garde++ < 30) {
      const style = getComputedStyle(el);

      // Un fond posé par le thème (schémas de couleur) est le repère le plus sûr.
      const couleur = style.backgroundColor;
      const m = couleur && couleur.match(/rgba?\(([^)]+)\)/);
      if (m) {
        const p = m[1].split(',').map((n) => parseFloat(n));
        const alpha = p.length > 3 ? p[3] : 1;
        if (alpha > 0.5) {
          const sombre = luminance(p[0], p[1], p[2]) < 0.5;
          return inverse ? !sombre : sombre;
        }
      }

      // Une image de fond masque ce qu'il y a derrière : on s'arrête et on
      // s'en remet à la couleur de texte du conteneur, généralement claire
      // sur les visuels sombres.
      if (style.backgroundImage && style.backgroundImage !== 'none') {
        const mc = style.color && style.color.match(/rgba?\(([^)]+)\)/);
        if (mc) {
          const c = mc[1].split(',').map((n) => parseFloat(n));
          const sombre = luminance(c[0], c[1], c[2]) > 0.5;
          return inverse ? !sombre : sombre;
        }
      }

      el = el.parentElement;
    }

    return inverse;
  };

  // Panneaux posés PAR-DESSUS la page : tiroirs du menu, du panier, des
  // favoris, filtres, recherche, ajout rapide.
  //
  // Pourquoi un test à part. La remontée ci-dessus part de l'élément survolé
  // et s'arrête au premier fond opaque. Or dans un tiroir, le fond est peint
  // par le panneau lui-même, qui n'est pas un ancêtre du lien survolé mais son
  // voisin : la remontée ne le croise jamais et file jusqu'au <body>, blanc.
  // Résultat, un tiroir sombre était toujours pris pour un fond clair, et le
  // slash s'y affichait en noir sur noir.
  //
  // On teste donc par géométrie, comme pour le footer : le pointeur est-il
  // dans le rectangle d'un panneau visible ? Le dernier de la liste l'emporte,
  // c'est celui qui est peint au-dessus. Un panneau fermé est hors écran ou de
  // taille nulle : il ne peut pas répondre par erreur.
  const PANNEAUX =
    'dialog[open], .menu-drawer, .cart-drawer__dialog, .wishlist-drawer__dialog, .search-modal__content, .quick-add-modal, .facets--drawer';

  const panneauSousLePointeur = () => {
    let trouve = null;

    for (const p of document.querySelectorAll(PANNEAUX)) {
      const r = p.getBoundingClientRect();
      if (r.width === 0 || r.height === 0) continue;
      if (cibleX < r.left || cibleX > r.right || cibleY < r.top || cibleY > r.bottom) continue;
      trouve = p;
    }
    if (!trouve) return null;

    const style = getComputedStyle(trouve);

    const m = style.backgroundColor.match(/rgba?\(([^)]+)\)/);
    if (m) {
      const p = m[1].split(',').map((n) => parseFloat(n));
      const alpha = p.length > 3 ? p[3] : 1;
      if (alpha > 0.5) return luminance(p[0], p[1], p[2]) < 0.5;
    }

    // Panneau sans fond propre : sa couleur de texte tranche forcément avec
    // le fond qu'il donne à voir. Un texte clair ne se pose que sur du sombre.
    const mc = style.color.match(/rgba?\(([^)]+)\)/);
    if (mc) {
      const c = mc[1].split(',').map((n) => parseFloat(n));
      return luminance(c[0], c[1], c[2]) > 0.5;
    }

    return null;
  };

  // Le footer en mode sombre : on teste si le pointeur est dans sa zone,
  // par géométrie pure. Aucune lecture de couleur, aucun :hover, aucun
  // elementFromPoint — uniquement les coordonnées dont la boucle dispose
  // déjà. C'est le seul test qui ne dépend pas de la façon dont le thème
  // fabrique son mode sombre (filtre d'inversion ou vraies couleurs).
  const pointeurSurFooterSombre = () => {
    const zones = document.querySelectorAll('.footer-mode--inverted');
    for (const z of zones) {
      const r = z.getBoundingClientRect();
      if (r.width === 0 || r.height === 0) continue;
      if (cibleX >= r.left && cibleX <= r.right && cibleY >= r.top && cibleY <= r.bottom) {
        return true;
      }
    }
    return false;
  };

  let compteur = 0;
  let sombreActuel = null;

  /* --- Boucle d'animation ----------------------------------------------- */

  // Suivi amorti : le glyphe rattrape la souris au lieu d'y être collé.
  // 0.22 donne un léger retard perceptible sans impression de lourdeur.
  const AMORTI = 0.22;

  const boucle = () => {
    x += (cibleX - x) * AMORTI;
    y += (cibleY - y) * AMORTI;
    curseur.style.transform = `translate3d(${x}px, ${y}px, 0)`;

    // La détection ne tourne qu'une frame sur six : elle est inutile à 60 Hz
    // et elle force un calcul de style.
    if (visible && ++compteur % 6 === 0) {
      // Ordre de priorité : un panneau ouvert masque tout ce qu'il y a
      // derrière, il tranche donc en premier. Vient ensuite le footer sombre,
      // puis la lecture du fond sous le pointeur.
      const panneau = panneauSousLePointeur();
      const sombre = panneau !== null ? panneau : pointeurSurFooterSombre() || fondSombre(cibleX, cibleY);
      if (sombre !== sombreActuel) {
        sombreActuel = sombre;
        curseur.classList.toggle('is-inverted', sombre);
      }
    }

    requestAnimationFrame(boucle);
  };
  requestAnimationFrame(boucle);

  // Si l'utilisateur branche une souris / active la réduction d'animations
  // en cours de session, on rend la main au curseur natif.
  const reevaluer = () => {
    const actif = fine.matches && !calme.matches;
    racine.classList.toggle('ag-cursor-on', actif);
    curseur.classList.toggle('is-visible', actif && visible);
  };
  fine.addEventListener('change', reevaluer);
  calme.addEventListener('change', reevaluer);
})();